import { useEffect, useState } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from 'react-router-dom';
import { useActiveUser } from './lib/cryptoys/users';
import PurchaseError from './pages/PurchaseError';
import RecaptchaError from './pages/RecaptchaError';
import { default as StripeStart } from './pages/stripe/Start';
import { default as StripeConfirmOrder } from './pages/stripe/ConfirmOrder';
import { default as SardineStart } from './pages/sardine/Start';
import { default as SardineConfirmOrder } from './pages/sardine/ConfirmOrder';
// import SelectCurrency from './pages/SelectCurrency';
import TimeExpired from './pages/TimeExpired';
import Success from './pages/Success';
import NotFound from './pages/NotFound';
import LinesFull from './pages/LinesFull';
import { CardProvider } from './utils/hooks/useCard';
import { PurchaseProvider } from './utils/hooks/usePurchase';
import { TimerProvider } from './utils/hooks/useTimer';
import TransientError from './pages/TransientError';
import { CRYPTOYS_LEGION_KEY, RECAPTCHA_ERROR_CODE } from './constants';
import { loadStripe } from '@stripe/stripe-js';
import { useMakeReservation } from './lib/cryptoys/drops/reserve';
import { useGetReservation } from './lib/cryptoys/drops/reservation';
import { redirectToCryptoysDrops } from './utils';
import Loading from './components/Loading/Loading';
import { useGetDrop } from './lib/cryptoys/drops/drops';
import { useGetLegion } from './lib/cryptoys/catalog/legions';
import backgroundImage from './img/zoo/PaymentBackground.webp';

// Calling `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

function App() {
  const dynamicRoutes = [
    process.env.REACT_APP_ROUTER_BASENAME,
    process.env.REACT_APP_ROUTER_BASENAME + '/',
    // process.env.REACT_APP_ROUTER_BASENAME + '/select-currency', // crypto option is currently disabled
    // process.env.REACT_APP_ROUTER_BASENAME + '/select-currency/', // crypto option is currently disabled
    process.env.REACT_APP_ROUTER_BASENAME + '/confirm',
    process.env.REACT_APP_ROUTER_BASENAME + '/confirm/',
    process.env.REACT_APP_ROUTER_BASENAME + '/cc-info',
    process.env.REACT_APP_ROUTER_BASENAME + '/cc-info/'
  ];
  const isDynamicRoute = dynamicRoutes.includes(window.location.pathname);
  const [recaptchaToken, setRecaptchaToken] = useState();
  const [isRecaptchaError, setIsRecaptchaError] = useState(false);
  const [isReservationComplete, setIsReservationComplete] = useState(false);
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError
  } = useActiveUser({
    recaptchaToken,
    isEnabled: isDynamicRoute
  });
  const [legionId, setLegionId] = useState();
  const [reservationId, setReservationId] = useState();
  const [correlationId, setCorrelationId] = useState();
  const [provider, setProvider] = useState();
  const [showInventoryReservedPage, setShowInventoryReservedPage] =
    useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const makeReservation = useMakeReservation();
  const searchParams = new URL(document.location).searchParams;
  const dropId = searchParams.get('dropId');

  const handleLoaded = (_) => {
    window.grecaptcha.ready((_) => {
      window.grecaptcha
        .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'drops_app_signup'
        })
        .then((token) => {
          setRecaptchaToken(token);
        });
    });
  };

  useEffect(() => {
    // Add reCaptcha
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.addEventListener('load', handleLoaded);
    document.body.appendChild(script);
  }, []);

  const {
    data: dropData,
    isLoading: isDropLoading,
    isError: isDropError,
    error: dropError
  } = useGetDrop({
    dropId,
    recaptchaToken,
    isEnabled: isDynamicRoute
  });

  const {
    data: legionData,
    isLoading: isLegionLoading,
    isError: isLegionError,
    error: legionError
  } = useGetLegion({
    legionId,
    recaptchaToken,
    isEnabled: isDynamicRoute
  });

  const {
    data: reservationData,
    isLoading: isReservationLoading,
    isError: isReservationError,
    error: reservationError
  } = useGetReservation({
    reservationId,
    recaptchaToken,
    isEnabled: isDynamicRoute
  });

  useEffect(() => {
    if (dropData) {
      setLegionId(dropData.data.legionId);
    }
  }, [dropData]);

  useEffect(() => {
    if (legionData) {
      if (legionData.imageUrls?.paymentBackground) {
        document.documentElement.style.setProperty(
          '--background-image',
          `url(${legionData.imageUrls.paymentBackground})`
        );
        // Setting image URLs in session storage so we don't need
        // to fetch them again when status pages are refreshed
        sessionStorage.setItem(
          CRYPTOYS_LEGION_KEY,
          JSON.stringify({ ...legionData.imageUrls, dropId })
        );
      }
    }
  }, [dropId, legionData]);

  const isError =
    isUserError ||
    isDropError ||
    isLegionError ||
    isReservationError ||
    showErrorPage;

  const isLoading =
    isDropLoading ||
    isLegionLoading ||
    (isReservationLoading && !makeReservation.isError) ||
    (makeReservation.isLoading && !isReservationError) ||
    isUserLoading;

  useEffect(() => {
    const legionImageAssets = JSON.parse(
      sessionStorage.getItem(CRYPTOYS_LEGION_KEY)
    );
    if (legionImageAssets && (!dropId || legionImageAssets.dropId === dropId)) {
      document.documentElement.style.setProperty(
        '--background-image',
        `url(${legionImageAssets['paymentBackground']})`
      );
    } else if (
      !document.documentElement.style.getPropertyValue('--background-image') &&
      (!isDynamicRoute || !isLoading)
    ) {
      document.documentElement.style.setProperty(
        '--background-image',
        `linear-gradient(rgba(2, 2, 3, 0.5), rgba(2, 2, 3, 0.5)),
      url(${backgroundImage})`
      );
    }
  }, [dropId, isDynamicRoute, isLoading]);

  useEffect(() => {
    // Once reservation data has been returned, determine if using Stripe or Sardine
    if (reservationData) {
      setProvider(reservationData.data.paymentProvider);
      if (reservationData.data.status === 'COMPLETED') {
        setIsReservationComplete(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationData]);

  useEffect(() => {
    // If a reservation has already been made update with info from search params
    // Otherwise make the reservation
    if (user && dropId && recaptchaToken) {
      const reservationIdParam = searchParams.get('reservationId');
      if (reservationIdParam) {
        setReservationId(reservationIdParam);
        setCorrelationId(searchParams.get('correlationId'));
      } else if (
        window.location.pathname === process.env.REACT_APP_ROUTER_BASENAME ||
        window.location.pathname === process.env.REACT_APP_ROUTER_BASENAME + '/' // reservation should only be made on 1st page
      ) {
        try {
          makeReservation.mutate(
            {
              dropId: dropId,
              recaptchaToken: recaptchaToken
            },
            {
              onSuccess: (reservationRes) => {
                setReservationId(reservationRes.data.reservationId);
                setCorrelationId(
                  reservationRes.headers.get('x-correlation-id')
                );
              },
              onError: (error) => {
                if (error.code === RECAPTCHA_ERROR_CODE) {
                  setIsRecaptchaError(true);
                } else if (error.code === 422) {
                  // all inventory is reserved
                  setShowInventoryReservedPage(true);
                } else if (error.code === 400) {
                  // drop is closed or inventory is sold out
                  // the legacy frontend will handle showing them the appropriate message
                  redirectToCryptoysDrops();
                } else {
                  setShowErrorPage(true);
                }
              }
            }
          );
        } catch (error) {
          setShowErrorPage(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dropId, recaptchaToken]);

  const getComponentToRender = (defaultComponent) => {
    // Use to show either the component, a loading, or error screen
    // Only use for dynamic routes
    if (
      isRecaptchaError ||
      userError?.code === RECAPTCHA_ERROR_CODE ||
      dropError?.code === RECAPTCHA_ERROR_CODE ||
      legionError?.code === RECAPTCHA_ERROR_CODE ||
      reservationError?.code === RECAPTCHA_ERROR_CODE
    ) {
      const queryString = reservationId
        ? `?reservationId=${reservationId}&correlationId=${correlationId}`
        : '';
      return <Navigate replace to={'/verification-error' + queryString} />;
    } else if (showInventoryReservedPage) {
      return <Navigate replace to={'/lines-full?dropId=' + dropId} />;
    } else if (isError) {
      return <Navigate replace to={'/error?dropId=' + dropId} />;
    } else if (isLoading) {
      return <Loading />;
    } else if (isReservationComplete) {
      return <Navigate replace to={'/success'} />;
    } else {
      return defaultComponent;
    }
  };

  const dropFlowArguments = {
    user,
    recaptchaToken,
    reservationId,
    correlationId,
    dropData,
    reservationData
  };

  const sardineStripeChildRoutes = [
    {
      path: '/',
      element: getComponentToRender(
        provider === 'stripe' ? (
          <StripeStart {...dropFlowArguments} />
        ) : (
          <SardineStart {...dropFlowArguments} />
        )
      )
    },
    // {
    //   path: '/select-currency',
    //   element: getComponentToRender(<SelectCurrency {...dropFlowArguments} />)
    // },
    {
      path: '/confirm',
      element: getComponentToRender(
        provider === 'stripe' ? (
          <StripeConfirmOrder
            {...dropFlowArguments}
            stripePromise={stripePromise}
          />
        ) : (
          <SardineConfirmOrder {...dropFlowArguments} />
        )
      )
    },
    {
      path: '/lines-full',
      element: <LinesFull />
    },
    {
      path: '/time-expired',
      element: <TimeExpired />
    },
    {
      path: '/error',
      element: <TransientError />
    },
    {
      path: '/verification-error',
      element: <RecaptchaError />
    },
    {
      path: '/purchase-error',
      element: <PurchaseError />
    },
    {
      path: '/success',
      element: <Success />
    },
    {
      path: '*',
      element: <NotFound />
    }
  ];

  const router = createBrowserRouter(
    [
      {
        errorElement: <TransientError />,
        children: sardineStripeChildRoutes
      }
    ],
    { basename: process.env.REACT_APP_ROUTER_BASENAME }
  );

  return (
    <>
      <div className="backgroundImage"></div>
      <TimerProvider reservationData={reservationData}>
        <PurchaseProvider>
          <CardProvider>
            <RouterProvider router={router} />
          </CardProvider>
        </PurchaseProvider>
      </TimerProvider>
    </>
  );
}

export default App;
